import X from "@/assets/icons/x.svg";
import Meetings from "@/components/widgets/meetings/Meetings";
import Email, { MailsListId } from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import { Tone } from "@/hooks/useGenerateAnswerWithIA";
import useGetMailThread from "@/hooks/useGetMailThread";
import useMailActions from "@/hooks/useMailActions";
import { cn, getEventsFromMailsList } from "@/lib/utils";
import { Editor } from "@tiptap/react";
import {
    Dispatch,
    RefObject,
    SetStateAction,
    useEffect,
    useMemo,
    useRef,
} from "react";
import GroupAvatars from "../mailsList/GroupAvatars";
import {
    FocusTarget,
    MailAction,
    MailActionVariant,
} from "../mailsList/MailAction";
import { Button } from "../ui/button";
import DocumentsLinksAndSimilar from "../widgets/attachements/DocumentsLinksAndSimilar";
import SmartTodos from "../widgets/todo/SmartTodos";
import MailInput from "../writeMail/MailInput";
import ContextBar, { ContextBarVariant, useMailContext } from "./ContextBar";
import {
    forwardAttachments,
    type ForwardAttachments,
} from "./forwardAttachments";
import MailFeed from "./MailFeed";
import MailSummary from "./MailSummary";

type MailThreadProps = {
    classname?: string;
    onClose?: () => void;
    mail?: Email;
    variant?: ContextBarVariant;
    handleProcessingFocusMail?: () => void;
    listId: MailsListId;
    setMailInputExpanded: (
        mailInputExpanded: boolean,
        focusTarget?: FocusTarget,
    ) => void;
    mailInputExpanded: boolean;
    editor: Editor;
    suggestionEditor: Editor;
    pendingSuggestion: boolean;
    setPendingSuggestion: Dispatch<SetStateAction<boolean>>;
    setSelectedMails?: Dispatch<SetStateAction<Email[]>>;
    toInputRef: RefObject<HTMLInputElement | null>;
    attachments: EmailAttachment[];
    setAttachments: Dispatch<SetStateAction<EmailAttachment[]>>;
    acceptOrRefuseSuggestion: (accept: boolean) => void;
    isLoading: boolean;
    isPendingChange: boolean;
    makeSuggestion: (action: Tone) => void;
    /**
     * In the assistant's side panel: one column, like the rest of the assistant — the subject, the
     * loop's summary, the thread, its actions and the reply, then the to-dos, documents and links.
     * The tab closes it (no close button of its own).
     */
    embedded?: boolean;
};

const MailThread = ({
    classname,
    onClose,
    mail,
    variant = ContextBarVariant.SQUEEZE,
    handleProcessingFocusMail,
    listId,
    setMailInputExpanded,
    mailInputExpanded,
    editor,
    suggestionEditor,
    pendingSuggestion,
    setPendingSuggestion,
    setSelectedMails,
    toInputRef,
    attachments,
    setAttachments,
    acceptOrRefuseSuggestion,
    isLoading,
    isPendingChange,
    makeSuggestion,
    embedded = false,
}: MailThreadProps) => {
    const mailsThread = useGetMailThread(mail);
    const { setReadStatus } = useMailActions();

    // Guard on mail identity, not isRead: otherwise "mark as unread" flips
    // isRead back and the effect immediately re-marks the mail read.
    const lastOpenedMailId = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!mail) {
            lastOpenedMailId.current = undefined;

            return;
        }
        if (mail.uniqueMailvistaId === lastOpenedMailId.current) return;
        lastOpenedMailId.current = mail.uniqueMailvistaId;
        if (!mail.isRead) {
            setReadStatus.mutateAsync({ mail, readStatus: true, listId });
        }
    }, [mail, listId]);

    const events = useMemo(
        () => getEventsFromMailsList(mailsThread.data?.mails ?? []),
        [mailsThread],
    );

    const enrichedMail = useMemo(
        () =>
            mailsThread.data?.mails.find(
                (threadMail) =>
                    threadMail.internetMessageId === mail?.internetMessageId,
            ),
        [mailsThread.data, mail?.internetMessageId],
    );

    const forward: ForwardAttachments = useMemo(
        () =>
            mail
                ? forwardAttachments(mail, enrichedMail)
                : { ready: true, attachments: [] },
        [mail, enrichedMail],
    );

    const forwardOriginalAttachments = forward.ready ? forward.attachments : [];

    const {
        summary,
        todos,
        areKeptTodos,
        areKeptTodosLoading,
        documents,
        links,
    } = useMailContext(mail, mailsThread.data);

    const meetings = events.length > 0 && (
        <Meetings
            events={events}
            mails={mailsThread.data?.mails}
            showOnlyLatest
        />
    );
    const feed = (
        <MailFeed
            isLoading={mailsThread.isLoading}
            mail={mail}
            mailsThread={mailsThread.data}
            selectedClassName={
                embedded ? "border border-assistant-borderStrong" : undefined
            }
        />
    );
    const actions = mail && (
        <MailAction
            handleProcessingFocusMail={handleProcessingFocusMail}
            listId={listId}
            mail={mail}
            setMailInputExpanded={setMailInputExpanded}
            setSelectedMails={setSelectedMails}
            variant={MailActionVariant.SINGLE_MAIL}
        />
    );
    const reply = (
        <MailInput
            key={mail?.internetMessageId}
            acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
            attachments={attachments}
            editor={editor}
            emailToReplyToOrForward={mail}
            // enriched body (inline cid: resolved to data:) for the forwarded quote;
            // passed separately to keep emailToReplyToOrForward stable (no draft reset)
            forwardBody={enrichedMail?.message}
            forwardAttachmentsReady={forward.ready}
            forwardOriginalAttachments={forwardOriginalAttachments}
            isLoading={isLoading}
            isPendingChange={isPendingChange}
            mailInputExpanded={mailInputExpanded}
            makeSuggestion={makeSuggestion}
            pendingSuggestion={pendingSuggestion}
            setAttachments={setAttachments}
            setMailInputExpanded={setMailInputExpanded}
            setPendingSuggestion={setPendingSuggestion}
            suggestionEditor={suggestionEditor}
            toInputRef={toInputRef}
        />
    );

    if (embedded) {
        return (
            <div
                className={cn(
                    "flex min-h-0 min-w-0 w-full flex-col overflow-hidden",
                    classname,
                )}
            >
                {/* Always in view: the subject and the loop's summary. Below, what scrolls (the
                    thread scrolls itself to the opened mail). */}
                <div className="flex flex-none flex-col gap-3 border-b border-assistant-divider px-4 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                        <h2 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-primary-darkest">
                            {mail?.subject}
                        </h2>
                        <GroupAvatars mail={mail} variant="noSender" />
                    </div>
                    {summary && <MailSummary content={summary} />}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="flex min-w-0 flex-col gap-3 px-4 py-3">
                        {meetings}
                        {feed}
                        {actions}
                        {reply}
                    </div>
                    {(todos.length > 0 ||
                        documents.length > 0 ||
                        links.length > 0) && (
                        <div className="flex min-w-0 flex-col gap-3 border-t border-assistant-divider px-4 py-3">
                            {todos.length > 0 && (
                                <SmartTodos
                                    areKeptTodos={areKeptTodos}
                                    isLoading={areKeptTodosLoading}
                                    todos={todos}
                                />
                            )}
                            <DocumentsLinksAndSimilar
                                documents={documents}
                                links={links}
                                mailsList={mailsThread.data}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn("flex min-w-0 w-full gap-2.5 pb-4 pr-3.5", classname)}
        >
            <div className="relative flex min-w-0 w-full flex-1 flex-col gap-3 overflow-hidden rounded-2xl bg-white px-6 py-4">
                {onClose && (
                    <div className="flex w-full items-center justify-end">
                        <Button
                            className="absolute right-4 top-4"
                            onClick={onClose}
                            size="smallCircle"
                            variant="action"
                        >
                            <img alt="close" className="h-4 w-4" src={X} />
                        </Button>
                    </div>
                )}
                <div className="flex w-full min-w-0 gap-5 pr-8 font-body text-sm font-semibold text-text-mainTitle">
                    <p className="min-w-0 truncate">{mail?.subject}</p>
                    <GroupAvatars mail={mail} variant="noSender" />
                </div>
                {meetings}
                {feed}
                {actions}
                {reply}
            </div>
            <div className="hidden h-full min-w-0 shrink flex-col gap-2 lg:flex">
                <ContextBar
                    mail={mail}
                    mailsThread={mailsThread.data}
                    variant={variant}
                />
            </div>
        </div>
    );
};

export default MailThread;
